import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAdminMode, useUser } from '../providers/UserProvider';
import { ReaderNavbar } from '../components/ReaderNavbar';
import { ReaderFooter } from '../components/ReaderFooter';
import { StoryList } from '../components/StoryList';
import { ForYouFeedPicker } from '../components/ForYouFeedPicker';
import { Toast } from '../components/Toast';
import {
  FOR_YOU_DEPTH,
  FOR_YOU_FULL_DEPTH,
  listForYouElements,
  type ForYouElement,
} from '../repositories/forYouRepository';
import {
  listSavedSearches,
  updateSavedSearch,
  type SavedSearch,
} from '../repositories/savedSearchRepository';
import { FOR_YOU_CHANNEL_ID } from '../utils/channelUrl';
import { setSavedSearchPresence } from '../utils/savedSearchPresence';
import { isAdmin } from '../auth';

/**
 * For You: every one of the reader's feeds, merged into one ranked list.
 *
 * The page reads a feed the half-hour producer has already built (see
 * `backend/app/services/for_you_feed.py`) rather than running the reader's
 * searches on load, so it paints deep and immediately and costs no judge
 * tokens. Excluding a feed is a write against that feed, not page-local state,
 * so the same exclusion holds on the reader's next device.
 */
export default function ForYouPage() {
  const { user, logout } = useUser();
  const { adminMode } = useAdminMode();
  const [searches, setSearches] = useState<SavedSearch[]>([]);
  const [elements, setElements] = useState<ForYouElement[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [deepened, setDeepened] = useState(false);
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(new Set());
  const [status, setStatus] = useState({ message: '', isError: false });

  const loadElements = useCallback(async (limit: number, bypassCache = false) => {
    setElements(await listForYouElements(limit, bypassCache));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [loadedSearches] = await Promise.all([listSavedSearches(), loadElements(FOR_YOU_DEPTH)]);
      setSearches(loadedSearches);
      setSavedSearchPresence(loadedSearches.length > 0);
    } catch (err) {
      setStatus({ message: (err as Error).message || 'Failed to load For You', isError: true });
    } finally {
      setLoading(false);
    }
  }, [loadElements]);

  useEffect(() => { load(); }, [load]);

  const toggleMembership = useCallback(
    async (search: SavedSearch, included: boolean) => {
      setPendingIds((prev) => new Set(prev).add(search.id));
      try {
        const updated = await updateSavedSearch(search.id, { in_for_you: included });
        setSearches((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
        await loadElements(deepened ? FOR_YOU_FULL_DEPTH : FOR_YOU_DEPTH, true);
      } catch (err) {
        setStatus({ message: (err as Error).message || 'Failed to update For You', isError: true });
      } finally {
        setPendingIds((prev) => {
          const next = new Set(prev);
          next.delete(search.id);
          return next;
        });
      }
    },
    [deepened, loadElements],
  );

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      await loadElements(FOR_YOU_FULL_DEPTH);
      setDeepened(true);
    } catch (err) {
      setStatus({ message: (err as Error).message || 'Failed to load more', isError: true });
    } finally {
      setLoadingMore(false);
    }
  }, [loadElements]);

  const dismissStatus = useCallback(() => setStatus({ message: '', isError: false }), []);
  const hasSearches = searches.length > 0;
  const includedCount = searches.filter((search) => search.in_for_you).length;
  const canDeepen = !deepened && elements.length >= FOR_YOU_DEPTH;

  return (
    <div className="reader-shell">
      <Toast message={status.message} isError={status.isError} onDismiss={dismissStatus} />
      <ReaderNavbar
        selectedChannelId={FOR_YOU_CHANNEL_ID}
        onSelectChannel={() => {}}
        user={user}
        onLogout={logout}
      />
      <main className="reader-main-full">
        {loading ? (
          <section className="feed-list-pane">
            <div className="empty-panel">Loading...</div>
          </section>
        ) : !hasSearches ? (
          <section className="feed-list-pane">
            <div className="empty-panel">
              <p>
                For You merges all of your feeds into one list. <Link to="/search/new">Save a
                search</Link> to build it.
              </p>
            </div>
          </section>
        ) : (
          <>
            <section className="feed-list-pane">
              <ForYouFeedPicker
                searches={searches}
                pendingIds={pendingIds}
                onToggle={toggleMembership}
              />
            </section>
            {includedCount === 0 ? (
              <section className="feed-list-pane">
                <div className="empty-panel">
                  Every one of your feeds is excluded. Check one above to see it here.
                </div>
              </section>
            ) : (
              <StoryList
                selectedChannelId={FOR_YOU_CHANNEL_ID}
                elements={elements}
                loading={false}
                isAdmin={isAdmin(user) && adminMode}
                eventContext={{ surface: 'channel', surfaceId: FOR_YOU_CHANNEL_ID }}
                onLoadMore={canDeepen ? loadMore : undefined}
                loadingMore={loadingMore}
              />
            )}
          </>
        )}
      </main>
      <ReaderFooter />
    </div>
  );
}
